import React, { useState, useEffect } from "react";
import { Y, T, CREDIT } from "../theme";
import { IcoGenerate, IcoCredits } from "../icons";
import { api } from "../firebase";
import { CreditIcon } from "./shared";
import { useNotify, useHeaderRight } from "../App";
import { useTheme } from "../useTheme";
import PageLoader from "./PageLoader";

/* ── Friendly model label ─────────────────────────────────────────────────── */
const MODEL_LABELS = {
  "kling-1.5":"1.5","kling-1.5-pro":"1.5 Pro",
  "kling-1.6":"1.6","kling-1.6-pro":"1.6 Pro",
  "kling-2.1":"2.1","kling-2.1-pro":"2.1 Pro",
  "kling-2.5-turbo":"2.5T","kling-2.5-turbo-pro":"2.5T Pro",
  "kling-2.6":"2.6","kling-2.6-pro":"2.6 Pro",
  "kling-3.0":"3.0","kling-3.0-pro":"3.0 Pro",
};
const ml = m => MODEL_LABELS[m] || m.replace("kling-","");

export default function DashboardView() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const { notify } = useNotify();
  const setError = (msg) => msg ? notify("error", msg, 6000) : null;
  const setHeaderRight = useHeaderRight();
  // Theme subscription so the header stat pills re-render on toggle.
  const { mode: themeMode } = useTheme();

  useEffect(() => {
    api.getUsage()
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  const narrow = window.innerWidth <= 600;
  const hFs = narrow ? 18 : 28;
  const hIco = narrow ? 16 : 20;
  const hGap = narrow ? 14 : 36;

  const stats = data ? [
    { icon:<IcoGenerate s={hIco} c={T.title}/>, label:"Generations", value: String(data.totalGenerations ?? 0), color:Y },
    { icon:<CreditIcon size={hIco*1.8} color={CREDIT}/>, label:"Credits Spent", value: String(data.totalCreditsSpent ?? 0), color:CREDIT },
  ] : [
    { icon:<IcoGenerate s={hIco} c={T.title}/>, label:"Generations", value:"—", color:Y },
    { icon:<CreditIcon size={hIco*1.8} color={CREDIT}/>, label:"Credits Spent", value:"—", color:CREDIT },
  ];

  // Register stats in page header
  useEffect(() => {
    setHeaderRight(
      <div style={{display:"flex",alignItems:"center",gap:hGap,marginRight:narrow?6:16}}>
        {stats.map((s,i) => (
          <div key={i} title={s.label} aria-label={s.label} style={{display:"flex",alignItems:"center",gap:narrow?4:6}}>
            <div style={{display:"flex",alignItems:"center"}} role="img" aria-label={s.label}>{s.icon}</div>
            <span style={{color:s.color,fontSize:hFs,fontWeight:900,letterSpacing:-0.5}}>{s.value}</span>
          </div>
        ))}
      </div>
    );
    return () => setHeaderRight(null);
  }, [data, loading, themeMode]);

  const models = data?.modelStats || [];

  return (
    <div style={{display:"flex",flexDirection:"column",gap:24}}>
      <PageLoader ready={!loading}/>

      {/* ── Kling Section ───────────────────────────────────────────────── */}
      <div style={{marginTop:8}}>
        {/* Kling logo header */}
        <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:20}}>
          <div style={{width:36,height:36,borderRadius:6,background:T.modelBadgeBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
            <div style={{width:25,height:25,borderRadius:5,background:"#000",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <img src="/kling-logo.png" style={{width:"100%",height:"100%",objectFit:"contain"}} alt="Kling"/>
            </div>
          </div>
          <span style={{color:T.text2,fontSize:26,fontWeight:800,letterSpacing:"0.02em"}}>Kling</span>
        </div>

        {/* Model success rates */}
        <div style={{color:T.text3,marginBottom:12,display:"flex",alignItems:"center"}} title="Model success rates">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
        </div>

        {loading ? (
          <div style={{color:T.text3,fontSize:12}}>Loading…</div>
        ) : models.length === 0 ? (
          <div style={{color:T.text3,fontSize:12}}>No generation data yet.</div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {models.map((m,i) => {
              const pct = m.successRate;
              const barColor = pct >= 90 ? T.success : pct >= 70 ? Y : pct >= 50 ? "#FF9933" : T.danger;
              return (
                <div key={i} style={{padding:"10px 14px",borderRadius:10,background:"rgba(255,255,255,0.03)"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                    <span style={{color:T.text2,fontSize:13,fontWeight:600}}>{ml(m.model)}</span>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <span style={{color:T.success,fontSize:11,fontFamily:"monospace"}}>{m.ready}</span>
                      <span style={{color:T.text3,fontSize:9}}>/</span>
                      <span style={{color:T.danger,fontSize:11,fontFamily:"monospace"}}>{m.failed}</span>
                      <span style={{color:barColor,fontSize:12,fontWeight:800,fontFamily:"monospace",minWidth:42,textAlign:"right"}}>{pct}%</span>
                    </div>
                  </div>
                  <div style={{background:"rgba(255,255,255,0.06)",borderRadius:3,height:3,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${pct}%`,background:barColor,borderRadius:3,transition:"width 0.8s ease"}}/>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Grok Section ──────────────────────────────────────────────── */}
      <div style={{marginTop:8}}>
        {/* Grok logo header */}
        <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:20}}>
          <div style={{width:36,height:36,borderRadius:6,background:T.modelBadgeBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
            <svg width={24} height={24} viewBox="0 0 512 510" fill="#fff"><path d="M115.6 0h280.8C459.97 0 512 52 512 115.6v278.4c0 63.6-52 115.6-115.6 115.6H115.6C52 509.6 0 457.6 0 394V115.6C0 52 52 0 115.6 0z" fill="#000"/><path d="M213.2 306l179-180v.2l51.7-51.8c-.9 1.3-1.9 2.6-2.8 3.9-39.3 54.2-58.5 80.6-43.1 146.9l-.1-.1c10.6 45.1-.7 95.1-37.4 131.8-46.2 46.3-120.2 56.6-181.1 14.9l42.5-19.7c38.9 15.3 81.4 8.6 111.9-22 30.6-30.6 37.4-75.2 22.1-112.3-2.9-7-11.7-8.8-17.8-4.3L213.2 306zm-25.8 22.4l-.1.1L68.1 435.2c7.6-10.4 17-20.3 26.3-30.1 26.4-27.8 52.7-55.4 36.7-94.3-21.4-52.1-9-113.2 30.7-152.9 41.2-41.3 102-51.7 152.7-30.8 11.2 4.2 21 10.1 28.6 15.6l-42.4 19.6c-39.4-16.6-84.6-5.3-112.2 22.3-37.3 37.3-44.8 102-1.1 143.8z" fill="#fff"/></svg>
          </div>
          <span style={{color:T.text2,fontSize:26,fontWeight:800,letterSpacing:"0.02em"}}>Grok</span>
        </div>

        {/* Model success rates */}
        {data?.grokModelStats && data.grokModelStats.length > 0 ? (
          <>
            <div style={{color:T.text3,marginBottom:12,display:"flex",alignItems:"center"}} title="Model success rates">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {data.grokModelStats.map((m,i) => {
                const pct = m.successRate;
                const barColor = pct >= 90 ? T.success : pct >= 70 ? Y : pct >= 50 ? "#FF9933" : T.danger;
                return (
                  <div key={i} style={{padding:"10px 14px",borderRadius:10,background:"rgba(255,255,255,0.03)"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                      <span style={{color:T.text2,fontSize:13,fontWeight:600}}>{m.model}</span>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <span style={{color:T.success,fontSize:11,fontFamily:"monospace"}}>{m.ready}</span>
                        <span style={{color:T.text3,fontSize:9}}>/</span>
                        <span style={{color:T.danger,fontSize:11,fontFamily:"monospace"}}>{m.failed}</span>
                        <span style={{color:barColor,fontSize:12,fontWeight:800,fontFamily:"monospace",minWidth:42,textAlign:"right"}}>{pct}%</span>
                      </div>
                    </div>
                    <div style={{background:"rgba(255,255,255,0.06)",borderRadius:3,height:3,overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${pct}%`,background:barColor,borderRadius:3,transition:"width 0.8s ease"}}/>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div style={{color:T.text3,fontSize:12}}>No Grok generation data yet.</div>
        )}
      </div>
    </div>
  );
}
